// packages/shared/src/rules/fruit.ts
import { tuning } from '@whiskerton/content';
import type { TileXY } from '../types/geom';
import type { MapData } from '../types/map';
import type { TreeState } from '../types/world';
import { groundAt, tierAt } from '../map/codec';

const DAY = 86_400_000;
export function treeStage(s: TreeState, now: number): 'sapling1' | 'sapling2' | 'sapling3' | 'mature' {
  if (s.plantedAt === null) return 'mature';
  const grow = tuning.fruit.growDays * DAY;
  const age = now - s.plantedAt;
  if (age >= grow) return 'mature';
  const stage = Math.min(tuning.fruit.saplingStages - 1, Math.max(0, Math.floor(age / (grow / tuning.fruit.saplingStages))));
  return (['sapling1', 'sapling2', 'sapling3'] as const)[stage]!;
}
export function canShake(s: TreeState, lastShakenAt: number | null, now: number): boolean {
  if (s.fruit === null || treeStage(s, now) !== 'mature') return false;
  return lastShakenAt === null || now - lastShakenAt >= tuning.fruit.cooldownDays * DAY;
}

// Front (south, toward the camera) first, then sides, back, diagonals; then the 5 × 5 ring by distance.
const RING1: readonly [number, number][] = [[0, 1], [1, 0], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]];
const RING2: readonly [number, number][] = (() => {
  const r: [number, number][] = [];
  for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (Math.max(Math.abs(dx), Math.abs(dy)) === 2) r.push([dx, dy]);
  return r.sort((a, b) => a[0] ** 2 + a[1] ** 2 - (b[0] ** 2 + b[1] ** 2) || b[1] - a[1] || b[0] - a[0]);
})();
export function fruitDropTiles(tree: TileXY, isFree: (t: TileXY) => boolean): TileXY[] {
  const out: TileXY[] = [];
  for (const [dx, dy] of [...RING1, ...RING2]) {
    const t = { x: tree.x + dx, y: tree.y + dy };
    if (isFree(t)) out.push(t);
    if (out.length === tuning.fruit.shakeDrop) break;
  }
  return out;
}
/** Spec §9.6 says "an empty grass tile"; the tier ≥ 1 check is deliberate (and pinned by §4.6 `fruit.plant`): the generator
 *  (§7.8) makes tier 0 the beach/shore band, and its few grass tiles border the sand where personal shells wash up and the
 *  sea where shadows sit, so trees there would block the beach. Every inland grass tile is tier ≥ 1. */
export function canPlantAt(map: MapData, tile: TileXY, isOccupied: (t: TileXY) => boolean): boolean {
  if (tile.x < 0 || tile.y < 0 || tile.x >= map.w || tile.y >= map.h) return false;
  return groundAt(map, tile.x, tile.y) === 'grass' && tierAt(map, tile.x, tile.y) >= 1 && !isOccupied(tile);
}
